"use client";

import { useCurrentUser } from "@/hooks/use-current-user";
import { pulseContext, type PulseEventHandler } from "@/hooks/use-pulse";
import type { PulseEvent, PulseMessage } from "@repo/utils/pulse";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

function makePulseUrl(sessionJWT: string): string | null {
  let base = process.env.NEXT_PUBLIC_PULSE_URL;
  if (!base) {
    return null;
  }

  base = base.replace("https://", "wss://").replace("http://", "ws://");
  return `${base}/ws?token=${sessionJWT}`;
}

function debugPrint(...data: any[]) {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  console.log("[Pulse]", ...data);
}

export function getPulseReconnectDelay(
  attempt: number,
  random = Math.random,
): number {
  const baseDelay = Math.min(
    30_000,
    500 * 2 ** Math.min(Math.max(attempt, 0), 6),
  );
  return baseDelay + Math.floor(random() * baseDelay * 0.3);
}

export function PulseProvider({ children }: PropsWithChildren) {
  const [socket, setSocket] = useState<WebSocket | null>(null);

  const { user, loaded } = useCurrentUser();
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const intentionalCloseRef = useRef(false);
  const lastHeartbeatRef = useRef(0);
  const desiredSubscriptionsRef = useRef(new Map<string, PulseMessage>());

  const eventHandlerStore = useRef(
    new Map<string, Set<PulseEventHandler<any>>>(),
  ).current;

  const sendMessage = useCallback((payload: PulseMessage) => {
    if (payload.type === "SUBSCRIBE_TO_CONVERSATION") {
      desiredSubscriptionsRef.current.set(
        `conversation:${payload.conversationId}`,
        payload,
      );
    } else if (payload.type === "SUBSCRIBE_TO_INTAKE") {
      desiredSubscriptionsRef.current.set(
        `intake:${payload.intakeSessionId}`,
        payload,
      );
    } else if (payload.type === "UNSUBSCRIBE_FROM_CONVERSATION") {
      desiredSubscriptionsRef.current.delete(
        `conversation:${payload.conversationId}`,
      );
    } else if (payload.type === "UNSUBSCRIBE_FROM_INTAKE") {
      desiredSubscriptionsRef.current.delete(
        `intake:${payload.intakeSessionId}`,
      );
    }

    const currentSocket = socketRef.current;
    if (!currentSocket || currentSocket.readyState !== WebSocket.OPEN) {
      if (!payload.type.startsWith("UNSUBSCRIBE")) {
        debugPrint(
          "Queued Pulse command until the socket reconnects:",
          payload,
        );
      }
      return;
    }

    currentSocket.send(JSON.stringify(payload));
    debugPrint("Sent Pulse message:", payload);
  }, []);

  const on = useCallback(
    <T extends PulseEvent>(event: T["event"], cb: PulseEventHandler<T>) => {
      if (!eventHandlerStore.has(event)) {
        eventHandlerStore.set(event, new Set());
      }

      eventHandlerStore.get(event)?.add(cb);
      debugPrint(`Added handler for event: ${event}`, cb);
    },
    [eventHandlerStore],
  );

  const off = useCallback(
    <T extends PulseEvent>(event: T["event"], cb: PulseEventHandler<T>) => {
      if (!eventHandlerStore.has(event)) {
        return;
      }

      eventHandlerStore.get(event)?.delete(cb);
      debugPrint(`Removed handler for event: ${event}`, cb);
    },
    [eventHandlerStore],
  );

  useEffect(() => {
    const sessionJWT = user?.sessionJWT;
    if (!loaded || !user || !sessionJWT) {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      socketRef.current?.close();
      socketRef.current = null;
      setSocket(null);
      return;
    }

    const url = makePulseUrl(sessionJWT);
    if (!url) {
      console.warn("Pulse URL not set");
      return;
    }

    intentionalCloseRef.current = false;

    const connect = () => {
      if (intentionalCloseRef.current) return;
      const currentSocket = socketRef.current;
      if (
        currentSocket &&
        (currentSocket.readyState === WebSocket.OPEN ||
          currentSocket.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      const ws = new WebSocket(url);
      socketRef.current = ws;
      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        lastHeartbeatRef.current = Date.now();
        setSocket(ws);
        debugPrint("Established Pulse connection.");
        for (const subscription of desiredSubscriptionsRef.current.values()) {
          ws.send(JSON.stringify(subscription));
        }
      };
      ws.onclose = () => {
        if (socketRef.current === ws) {
          socketRef.current = null;
          setSocket(null);
        }
        if (intentionalCloseRef.current) return;
        const attempt = reconnectAttemptRef.current++;
        const delay = getPulseReconnectDelay(attempt);
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          connect();
        }, delay);
        debugPrint(`Pulse disconnected; reconnecting in ${delay}ms.`);
      };
      ws.onerror = () => {
        debugPrint("Pulse socket error.");
      };
      ws.onmessage = async (event) => {
        let data: PulseEvent | { ok: boolean; error?: string };
        try {
          data = JSON.parse(event.data);
        } catch {
          debugPrint("Ignoring invalid Pulse message.");
          return;
        }
        if ("ok" in data) {
          lastHeartbeatRef.current = Date.now();
          if (data.ok === false) {
            console.error("[Pulse] Command failed:", data.error);
          }
          return;
        }
        if (!data.event) {
          debugPrint(data);
          return;
        }
        const handlers = eventHandlerStore.get(data.event);
        handlers?.forEach(async (handler) => {
          await handler(data);
        });
      };
      const heartbeat = window.setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return;
        if (Date.now() - lastHeartbeatRef.current > 45_000) {
          ws.close();
          return;
        }
        ws.send(
          JSON.stringify({
            type: "PING",
            requestId: crypto.randomUUID(),
          }),
        );
      }, 20_000);
      ws.addEventListener("close", () => window.clearInterval(heartbeat), {
        once: true,
      });
    };

    connect();
    const handleOnline = () => connect();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") connect();
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      intentionalCloseRef.current = true;
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      socketRef.current?.close();
      socketRef.current = null;
      setSocket(null);
    };
  }, [eventHandlerStore, loaded, user?.sessionJWT]);

  const contextValue = useMemo(
    () => ({
      socket,
      sendMessage,
      on,
      off,
    }),
    [off, on, sendMessage, socket],
  );

  return (
    <pulseContext.Provider value={contextValue}>
      {children}
    </pulseContext.Provider>
  );
}
